import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

export type ProfileRecord = {
    name: string;
    organisation: string;
    startDate: string; // year
    endDate: string | null; // either a year, or 'present
    iconUrl?: string;
};

export type Qualification = ProfileRecord;
export type Experience = ProfileRecord;
export type Education = ProfileRecord;

export type ExpertInsurer = {
    organisationId: number;
    organisationName: string;
    displayName: string;
    imageMimeType?: string;
    imageBase64?: string;
};

export type SimpleProfile = {
    firstName: string;
    lastName: string;
    title?: string;
    jobTitle: string;
    headline: string;
    topSpecialisms: string[];
    isPublic: boolean;
};

export type FullProfile = {
    firstName: string;
    lastName: string;
    title?: string;
    pronouns?: string;
    jobTitle: string;
    languages: string[];
    headline: string;
    aboutMe: string;
    location?: string;
    professionalQualifications?: string[];
    topSpecialisms: string[];
    otherTopics: string[];
    treatmentApproaches: string[];
    qualifications: Qualification[];
    experience: Experience[];
    education: Education[];
    isPublic: boolean;
    userType: 'expert' | 'partner';
    externalId: string;
    cognitoId: string;
};

export type PutPartnerProfileRequest = {
    firstName: string;
    lastName: string;
    title?: string;
    pronouns?: string;
    jobTitle: string;
    headline: string;
    aboutMe: string;
    location?: string;
    presentations: string[];
};

export type FullProfileIfNotModified = FullProfile | undefined;

// API Request and Response Types

export type ProfilePhoto = {
    blob: Blob;
    type: string;
};

export type PutProfileRequest = {
    patch: Partial<PutPartnerProfileRequest>;
};

export type PutProfilePhotoRequest = {
    mimeType: string;
};

export type GetInsurersResponse = {
    insurers: ExpertInsurer[];
};

export type GetConnectionsForExpertRequest = {
    following: string[];
    followedBy: string[];
};

export type GetProfilePhotoResponse = {
    presignedGetUrl: string;
};

export type PutProfilePhotoResponse = {
    presignedPutUrl: string;
};

export type PutSuccessResponse = {
    ok: boolean;
    errorMessage?: string;
};

export type BaseTileProps = {
    isLoading: boolean;
    isOwnProfile?: boolean;
};

export type AmplifyBaseQueryError = FetchBaseQueryError;

/**
 * Type predicate to narrow an unknown error to `FetchBaseQueryError`
 */
export function isAmplifyBaseQueryError(error: unknown): error is AmplifyBaseQueryError {
    return typeof error === 'object' && error != null && 'status' in error;
}

/**
 * Type predicate to narrow an unknown error to an object with a string 'message' property
 */
export function isErrorWithMessage(error: unknown): error is { message: string } {
    return (
        typeof error === 'object' &&
        error != null &&
        'message' in error &&
        typeof (error as Record<string, unknown>).message === 'string'
    );
}

type Topic = {
    topicId: number;
    isSpecialist: boolean;
    notes?: string | null;
};

type Insurer = {
    insuranceOrgId: number;
    insuranceNumber?: string | null;
    hcpcNumber?: string | null;
};

export type Expert = {
    cognitoId: string;
    internalId: string;
    subId: string;
    externalId: string;
    createdAt: number;
    lastModified: number;
    firstName: string;
    lastName: string;
    jobRole: string;
    jobRoleId?: number;
    jobRoleLabel?: string;
    profilePhoto?: string;
    slug?: string | null;
    biography: string;
    isEmployee: boolean;
    clinicalDirector?: {
        expertId: string;
        firstName: string;
        lastName: string;
        profilePhoto: string;
    } | null;
    referToSelfStatus: 'A' | 'N' | 'W';
    openToNewGeneralWorkOffers: boolean;
    openToNewSelectedWorkOffers: boolean;
    canConnect?: boolean;
    topics: Topic[];
    insurers: Insurer[];
};
